import { Check, AlertTriangle } from 'lucide-react'

import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import type { PermissionAlreadyInherited } from '@/types/api/permission'

import { SCOPE_LABELS, sourceLabel, type CatalogIndex } from './permissionCatalog'

interface DuplicateOverrideDialogProps {
  conflict: PermissionAlreadyInherited | null
  index: CatalogIndex | null
  userName: string
  pending?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Shown when a user override would duplicate access the user already has through a role
 * (backend 409 PERMISSION_ALREADY_INHERITED). Explains the trade-off before adding it anyway.
 */
export function DuplicateOverrideDialog({ conflict, index, userName, pending, onConfirm, onCancel }: DuplicateOverrideDialogProps) {
  return (
    <ConfirmDialog
      open={!!conflict}
      onOpenChange={(open) => !open && onCancel()}
      title="Already granted through a role"
      description={`${userName} already has this access. Add a separate user override anyway?`}
      confirmLabel="Add override anyway"
      pending={pending}
      onConfirm={onConfirm}
    >
      <div className="space-y-4 text-sm">
        <ul className="space-y-2">
          {conflict?.covered.map((c, i) => (
            <li key={i} className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <p className="font-medium text-neutral-800">
                {index?.actionByKey.get(c.permissionKey)?.label ?? c.permissionKey}
                <span className="font-normal text-neutral-500"> · {SCOPE_LABELS[c.scope]}</span>
              </p>
              <p className="text-xs text-neutral-500">
                Already granted by: {c.coveredBy.map((s) => sourceLabel(s)).join(', ')}
              </p>
            </li>
          ))}
        </ul>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-700">
              <Check className="h-3.5 w-3.5" /> Pros
            </p>
            <ul className="list-disc space-y-1 pl-4 text-xs text-neutral-600">
              <li>Access stays if the user is removed from the role or the role changes.</li>
              <li>Records that this user needs the access specifically.</li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" /> Cons
            </p>
            <ul className="list-disc space-y-1 pl-4 text-xs text-neutral-600">
              <li>Removing the role will no longer remove this access; the override must be revoked separately.</li>
              <li>Two sources for the same access make reviews and audits harder.</li>
            </ul>
          </div>
        </div>
      </div>
    </ConfirmDialog>
  )
}
